import { useEffect, useRef, useState } from 'react';
import { Mic } from 'lucide-react';

/** Accessible MediaRecorder wrapper. Nothing is uploaded until the user chooses to save. */
export function Recorder({ onRecorded, maxSeconds = 600, label = 'Record' }: { onRecorded: (blob: Blob, seconds: number) => void; maxSeconds?: number; label?: string }) {
  const [state, setState] = useState<'idle' | 'recording' | 'unsupported' | 'denied'>(
    typeof window !== 'undefined' && 'MediaRecorder' in window ? 'idle' : 'unsupported',
  );
  const [seconds, setSeconds] = useState(0);
  const rec = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timer = useRef<number | null>(null);
  const started = useRef(0);

  useEffect(() => () => stopTracks(), []);

  function stopTracks() {
    if (timer.current) window.clearInterval(timer.current);
    rec.current?.stream.getTracks().forEach((t) => t.stop());
  }

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const type = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find((t) => MediaRecorder.isTypeSupported(t));
      const r = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      chunks.current = [];
      r.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      r.onstop = () => {
        const blob = new Blob(chunks.current, { type: (r.mimeType || 'audio/webm').split(';')[0] });
        stopTracks();
        setState('idle');
        onRecorded(blob, Math.max(1, Math.round((Date.now() - started.current) / 1000)));
      };
      rec.current = r;
      started.current = Date.now();
      setSeconds(0);
      r.start();
      setState('recording');
      timer.current = window.setInterval(() => {
        const s = Math.round((Date.now() - started.current) / 1000);
        setSeconds(s);
        if (s >= maxSeconds) r.stop();
      }, 500);
    } catch {
      setState('denied');
    }
  }

  if (state === 'unsupported') return <p className="muted small">Recording is not supported in this browser — you can upload a file instead.</p>;
  return (
    <div className="recorder">
      {state === 'recording' ? (
        <>
          <span className="rec-dot" aria-hidden="true" />
          <span aria-live="polite">
            Recording… {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}
          </span>
          <button type="button" className="btn danger small" onClick={() => rec.current?.stop()}>
            Stop
          </button>
        </>
      ) : (
        <button type="button" className="btn warm small" onClick={start}>
          <Mic size={16} strokeWidth={1.75} aria-hidden="true" /> {label}
        </button>
      )}
      {state === 'denied' && <span className="small" role="alert">Microphone access was blocked. Allow it in your browser settings to record.</span>}
    </div>
  );
}
